import React, { useEffect, useState, useMemo } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, orderBy, serverTimestamp, doc, getDoc, onSnapshot, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Typography, TextField, Button, Card, CardContent, List, Box, ListItem, ListItemText, IconButton, Collapse, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  Select, MenuItem, FormControl, InputLabel, ToggleButtonGroup, ToggleButton, Chip, Link as MuiLink, CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from '../styles/Dashboard.module.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Financeiro = () => {
  const [contas, setContas] = useState([]);
  const [novaConta, setNovaConta] = useState({ descricao: '', valor: '', tipo: 'a_pagar' });
  const [anexo, setAnexo] = useState(null);
  const [dataVencimento, setDataVencimento] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [uploading, setUploading] = useState(false);

  // Estados para filtros
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroDataInicio, setFiltroDataInicio] = useState(null);
  const [filtroDataFim, setFiltroDataFim] = useState(null);
  const [isGraficoVisivel, setIsGraficoVisivel] = useState(false); // Inicia minimizado

  // Estados para o Dialog de confirmação de pagamento
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedConta, setSelectedConta] = useState(null);
  const [valorPago, setValorPago] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!condominioId) return;

    const q = query(collection(db, 'tb_contas'), where('condominio_id', '==', condominioId), orderBy('created_at', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const contas_list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContas(contas_list);
    }, (error) => {
      console.error("Erro ao buscar contas:", error);
    });

    return () => unsubscribe();
  }, [condominioId]);

  const handleCreateConta = async () => {
    if (!novaConta.descricao || !novaConta.valor || !dataVencimento) {
      alert('Por favor, preencha a descrição, valor e data de vencimento.');
      return;
    }

    setUploading(true);
    try {
      // 1. Cria o registro no Firestore para obter um ID
      const docRef = await addDoc(collection(db, 'tb_contas'), {
        ...novaConta,
        valor: parseFloat(novaConta.valor),
        condominio_id: condominioId,
        data_vencimento: dataVencimento,
        status: 'pendente',
        created_at: serverTimestamp(),
        anexo_url: '',
        anexo_path: '',
      });

      let anexoUrl = '';
      let anexoPath = '';

      // 2. Se houver anexo, faz o upload para o Storage
      if (anexo) {
        const fileExtension = anexo.name.split('.').pop();
        anexoPath = `contas/${condominioId}/${docRef.id}.${fileExtension}`;
        const fileRef = ref(getStorage(), anexoPath);
        const metadata = {
          customMetadata: {
            'userRole': userRole,
            'condominioId': condominioId,
            'ownerId': auth.currentUser.uid
          }
        };

        await uploadBytes(fileRef, anexo, metadata);
        anexoUrl = await getDownloadURL(fileRef);
      }

      // 3. Atualiza o registro com as informações do anexo
      await updateDoc(docRef, {
        anexo_url: anexoUrl,
        anexo_path: anexoPath,
      });

      setNovaConta({ descricao: '', valor: '', tipo: 'a_pagar' });
      setDataVencimento(null);
      setAnexo(null);
      alert('Conta adicionada com sucesso!');
    } catch (error) {
      console.error("Erro ao adicionar conta:", error);
      alert('Erro ao adicionar conta: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClickStatus = (conta) => {
    if (conta.status === 'pendente') {
      setSelectedConta(conta);
      setValorPago(conta.valor.toString()); // Preenche o valor original
      setDialogOpen(true);
    } else {
      // Reverte para pendente
      if (window.confirm('Deseja reverter este lançamento para "Pendente"?')) {
        const contaRef = doc(db, 'tb_contas', conta.id);
        updateDoc(contaRef, {
          status: 'pendente',
          valor_pago: null,
          data_pagamento: null
        });
      }
    }
  };

  const handleConfirmarPagamento = async () => {
    if (!valorPago || isNaN(parseFloat(valorPago))) {
      alert('Por favor, insira um valor de pagamento válido.');
      return;
    }
    const contaRef = doc(db, 'tb_contas', selectedConta.id);
    await updateDoc(contaRef, {
      status: 'pago',
      valor_pago: parseFloat(valorPago),
      data_pagamento: serverTimestamp()
    });
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedConta(null);
    setValorPago('');
  };

  const handleDeleteConta = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta conta?')) return;
    try {
      const token = await auth.currentUser.getIdToken();

      // ...

      // ...
      const response = await fetch(`${getApiUrl()}/api/contas/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao deletar lançamento.');
      }

      alert('Lançamento deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar conta:', error);
      alert('Erro ao deletar conta: ' + error.message);
    }
  };

  const formatarData = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString('pt-BR');
  };

  const formatarValor = (valor) => {
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const contasFiltradasPorData = useMemo(() => {
    if (!filtroDataInicio && !filtroDataFim) {
      return contas;
    }
    return contas.filter(conta => {
      if (!conta.data_vencimento) return false;
      const dataConta = conta.data_vencimento.toDate();

      const matchDataInicio = !filtroDataInicio || dataConta >= filtroDataInicio;
      const matchDataFim = !filtroDataFim || dataConta <= filtroDataFim;

      return matchDataInicio && matchDataFim;
    });
  }, [contas, filtroDataInicio, filtroDataFim]);

  const contasFiltradas = contasFiltradasPorData.filter(conta => {
    const matchTipo = filtroTipo === 'todos' || conta.tipo === filtroTipo;
    const matchStatus = filtroStatus === 'todos' || conta.status === filtroStatus;
    return matchTipo && matchStatus;
  });

  const fluxoDeCaixaData = useMemo(() => {
    // O gráfico agora usa as contas já filtradas (por data, tipo e status)
    // e considera o valor_pago para as contas pagas.
    const dataPorMes = contasFiltradas
      .filter(conta => conta.status === 'pago' && conta.data_vencimento)
      .reduce((acc, conta) => {
        const data = conta.data_vencimento.toDate();
        const valorRealizado = conta.valor_pago ?? conta.valor; // Usa valor_pago se existir
        // Formato 'YYYY-MM' para facilitar a ordenação
        const mesAno = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;

        if (!acc[mesAno]) {
          acc[mesAno] = { entradas: 0, saidas: 0 };
        }

        if (conta.tipo === 'a_receber') {
          acc[mesAno].entradas += valorRealizado;
        } else {
          acc[mesAno].saidas += valorRealizado;
        }

        return acc;
      }, {});

    const labels = Object.keys(dataPorMes).sort();
    const data = {
      labels: labels.map(label => label.split('-').reverse().join('/')), // Formata para 'MM/YYYY'
      datasets: [
        {
          label: 'Entradas',
          data: labels.map(mes => dataPorMes[mes].entradas),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
        },
        {
          label: 'Saídas',
          data: labels.map(mes => dataPorMes[mes].saidas),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
        },
      ],
    };
    return data;
  }, [contasFiltradas]);

  const resumoFinanceiro = useMemo(() => {
    // O resumo agora é calculado com base nas contas já filtradas pela data e considera o valor_pago
    return contasFiltradasPorData.reduce((acc, conta) => {
      if (conta.status === 'pago') {
        const valorRealizado = conta.valor_pago ?? conta.valor;
        if (conta.tipo === 'a_receber') {
          acc.saldo += valorRealizado;
        } else { // a_pagar
          acc.saldo -= valorRealizado;
        }
      } else { // pendente
        if (conta.tipo === 'a_receber') {
          acc.aReceber += conta.valor;
        } else { // a_pagar
          acc.aPagar += conta.valor;
        }
      }
      return acc;
    }, { saldo: 0, aPagar: 0, aReceber: 0 });
  }, [contasFiltradasPorData]);

  const limparFiltros = () => {
    setFiltroTipo('todos');
    setFiltroStatus('todos');
    setFiltroDataInicio(null);
    setFiltroDataFim(null);
  };

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);

  if (!isAdmin) {
    return (
      <Box>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Typography variant="h4">Acesso Negado</Typography>
          <Typography>Você não tem permissão para acessar esta página.</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>

      {/* Dialog de Confirmação de Pagamento */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Confirmar Pagamento</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirme ou ajuste o valor pago para o lançamento: "{selectedConta?.descricao}".
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="valor_pago"
            label="Valor Pago (R$)"
            type="number"
            fullWidth
            variant="standard"
            value={valorPago}
            onChange={(e) => setValorPago(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleConfirmarPagamento} variant="contained">Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Controle Financeiro</Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Saldo Atual (Realizado)</Typography>
                <Typography variant="h5" component="div" sx={{ color: resumoFinanceiro.saldo >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                  {formatarValor(resumoFinanceiro.saldo)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total a Receber (Pendente)</Typography>
                <Typography variant="h5" component="div" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {formatarValor(resumoFinanceiro.aReceber)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Total a Pagar (Pendente)</Typography>
                <Typography variant="h5" component="div" color="error.main" sx={{ fontWeight: 'bold' }}>
                  {formatarValor(resumoFinanceiro.aPagar)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Adicionar Lançamento</Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <TextField fullWidth label="Descrição" value={novaConta.descricao} onChange={(e) => setNovaConta({ ...novaConta, descricao: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField fullWidth label="Valor (R$)" type="number" value={novaConta.valor} onChange={(e) => setNovaConta({ ...novaConta, valor: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={novaConta.tipo} label="Tipo" onChange={(e) => setNovaConta({ ...novaConta, tipo: e.target.value })}>
                    <MenuItem value="a_pagar">A Pagar</MenuItem>
                    <MenuItem value="a_receber">A Receber</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2.5}>
                <DatePicker label="Data de Vencimento" value={dataVencimento} onChange={setDataVencimento} sx={{ width: '100%' }} />
              </Grid>
              <Grid item xs={12} md={1.5} sx={{ display: 'flex', alignItems: 'center' }}>
                {!anexo ? (
                  <Button variant="outlined" component="label" fullWidth sx={{ height: '56px' }}>
                    Anexar
                    <input type="file" hidden accept="application/pdf,image/*" onChange={(e) => setAnexo(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip
                    label={anexo.name}
                    onDelete={() => setAnexo(null)}
                    sx={{ maxWidth: '100%' }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="contained" onClick={handleCreateConta} disabled={uploading} fullWidth sx={{ height: '56px' }}>
                  {uploading ? <CircularProgress size={24} /> : 'Adicionar'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsGraficoVisivel(!isGraficoVisivel)}>
              <Typography variant="h5" className={styles.sectionTitle}>Fluxo de Caixa (Realizado)</Typography>
              <IconButton
                aria-expanded={isGraficoVisivel}
                aria-label="mostrar gráfico"
              >
                <ExpandMoreIcon sx={{ transform: isGraficoVisivel ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
              </IconButton>
            </Box>
            <Collapse in={isGraficoVisivel} timeout="auto" unmountOnExit>
              <Box sx={{ height: '400px', mt: 2 }}>
                <Bar data={fluxoDeCaixaData} options={{ maintainAspectRatio: false, responsive: true }} />
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Lançamentos</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item>
                <ToggleButtonGroup size="small" value={filtroTipo} exclusive onChange={(e, v) => v && setFiltroTipo(v)}>
                  <ToggleButton value="todos">Todos</ToggleButton>
                  <ToggleButton value="a_pagar">A Pagar</ToggleButton>
                  <ToggleButton value="a_receber">A Receber</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item>
                <ToggleButtonGroup size="small" value={filtroStatus} exclusive onChange={(e, v) => v && setFiltroStatus(v)}>
                  <ToggleButton value="todos">Todos</ToggleButton>
                  <ToggleButton value="pendente">Pendentes</ToggleButton>
                  <ToggleButton value="pago">Pagos</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item><DatePicker label="Data Início" value={filtroDataInicio} onChange={setFiltroDataInicio} slotProps={{ textField: { size: 'small' } }} /></Grid>
              <Grid item><DatePicker label="Data Fim" value={filtroDataFim} onChange={setFiltroDataFim} slotProps={{ textField: { size: 'small' } }} /></Grid>
              <Grid item>
                <Button variant="text" onClick={limparFiltros}>
                  Limpar Filtros
                </Button>
              </Grid>
            </Grid>
            <List>
              {contasFiltradas.map((conta) => (
                <ListItem key={conta.id} divider secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="status" onClick={() => handleClickStatus(conta)} title={conta.status === 'pendente' ? 'Marcar como Pago' : 'Marcar como Pendente'}>
                      <CheckCircleIcon color={conta.status === 'pago' ? 'success' : 'action'} />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteConta(conta.id)} sx={{ ml: 1 }}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ color: conta.tipo === 'a_pagar' ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                          {formatarValor(conta.valor_pago ?? conta.valor)}
                        </Typography>
                        <Typography>{conta.descricao}</Typography>
                      </Box>
                    }
                    secondary={
                      // The secondaryTypographyProps tells ListItemText to render a div instead of a p, fixing the nesting error.
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2">Vencimento: {formatarData(conta.data_vencimento)}</Typography>
                        <Chip
                          label={conta.status}
                          color={conta.status === 'pago' ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                        {conta.anexo_url && (
                          <MuiLink href={conta.anexo_url} target="_blank" rel="noopener noreferrer" variant="body2">Ver Anexo</MuiLink>
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Financeiro;
